// useWorkbench — wires the pure docket state machine to the API.
//
// Honesty plumbing: "Run checks" and "Log the repair" both flush dirty
// files FIRST, so the backend always evaluates exactly what the learner
// sees in the editor. Saves also auto-flush on a quiet 800ms debounce.
import { useCallback, useEffect, useReducer, useRef } from 'react';

import { apiService, handleApiError } from '../../services/api';
import { initialState, newlyPassed, reducer } from './workbenchState';

const SAVE_DEBOUNCE_MS = 800;

export default function useWorkbench({ courseId, domain, sessionId }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const sessionIdRef = useRef(sessionId || null);
  const saveTimer = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  // Names of checks that just flipped green — consumed by the rise motion.
  const flippedRef = useRef([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = sessionIdRef.current
          ? await apiService.getWorkbench(sessionIdRef.current)
          : await apiService.startWorkbench(courseId, domain);
        if (cancelled) return;
        sessionIdRef.current = res.data.session.id;
        dispatch({ type: 'loaded', session: res.data.session });
      } catch (err) {
        if (!cancelled) dispatch({ type: 'load_failed', error: handleApiError(err) });
      }
    })();
    return () => { cancelled = true; };
    // boot-once per mount; the harness remounts to start a new repair
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flushDirty = useCallback(async () => {
    const { dirty, contents } = stateRef.current;
    for (const path of Object.keys(dirty)) {
      await apiService.saveWorkbenchFile(sessionIdRef.current, path, contents[path]);
      dispatch({ type: 'saved', path });
    }
  }, []);

  const edit = useCallback((path, content) => {
    dispatch({ type: 'edited', path, content });
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      flushDirty().catch((err) =>
        dispatch({ type: 'save_failed', error: handleApiError(err) }),
      );
    }, SAVE_DEBOUNCE_MS);
  }, [flushDirty]);

  const selectFile = useCallback((path) => dispatch({ type: 'select_file', path }), []);

  const runChecks = useCallback(async () => {
    clearTimeout(saveTimer.current);
    dispatch({ type: 'checks_started' });
    try {
      await flushDirty();
      const res = await apiService.checkWorkbench(sessionIdRef.current);
      flippedRef.current = newlyPassed(
        stateRef.current.results,
        Object.fromEntries(res.data.results.map((r) => [r.check_id, r.passed])),
      );
      dispatch({ type: 'checks_landed', results: res.data.results });
    } catch (err) {
      dispatch({ type: 'checks_failed', error: handleApiError(err) });
    }
  }, [flushDirty]);

  const submit = useCallback(async () => {
    clearTimeout(saveTimer.current);
    dispatch({ type: 'submit_started' });
    try {
      await flushDirty();
      const res = await apiService.submitWorkbench(sessionIdRef.current);
      flippedRef.current = newlyPassed(
        stateRef.current.results,
        Object.fromEntries(res.data.results.map((r) => [r.check_id, r.passed])),
      );
      dispatch({
        type: 'submitted',
        grades: res.data.grades,
        feedback: res.data.feedback,
        solved: res.data.solved,
        results: res.data.results,
      });
    } catch (err) {
      dispatch({ type: 'submit_failed', error: handleApiError(err) });
    }
  }, [flushDirty]);

  const abandon = useCallback(async () => {
    try {
      await apiService.abandonWorkbench(sessionIdRef.current);
    } catch {
      /* leaving anyway */
    }
  }, []);

  return { state, edit, selectFile, runChecks, submit, abandon, flipped: flippedRef };
}
